import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Upload, FileText, Trash2, FileSpreadsheet } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import BulkImportDialog, { CANDIDATE_TEMPLATE } from "@/components/BulkImportDialog";
import { PeriodFilter, HRMultiFilter, inPeriod, matchesHR, useHROptions } from "@/components/PeriodFilter";

// Per-metric date (D): every stage is counted on ITS OWN date field, never mixed
const stageDate = (c, st) => {
  if (st === "screened") return c.screening_date || c.lead_date;
  if (st === "l1") return c.l1_date || c.lead_date;
  if (st === "l2") return c.l2_date || c.lead_date;
  if (st === "finalized") return c.decision_date || c.lead_date;
  if (st === "joined") return c.joining_date || c.lead_date;
  if (st === "rejected") {
    if (c.final_decision === "Rejected") return c.decision_date || c.lead_date;
    if (c.l2_status === "Rejected") return c.l2_date || c.lead_date;
    if (c.l1_status === "Rejected") return c.l1_date || c.lead_date;
    return c.screening_date || c.lead_date;
  }
  return c.lead_date;
};

const SOURCES = ["Naukri", "Referral", "Walk-in", "Social Media", "Consultancy", "Other"];
const SCREENING = ["Pending", "Done", "Rejected"];
const L_STATUS = ["", "Scheduled", "Cleared", "Selected", "Rejected"];
const FINAL = ["Pending", "Selected", "Rejected"];
const JOINING = ["Pending", "Done", "No Show"];

function badgeCls(status, type) {
  if (type === "screening") {
    if (status === "Done") return "rag-green";
    if (status === "Rejected") return "rag-red";
    return "rag-amber";
  }
  if (type === "final") {
    if (status === "Selected") return "rag-green";
    if (status === "Rejected") return "rag-red";
    return "rag-amber";
  }
  if (type === "joining") {
    if (status === "Done") return "rag-green";
    if (status === "No Show") return "rag-red";
    return "rag-amber";
  }
  return "rag-amber";
}

export default function Candidates() {
  const { user, branch, hrSelf } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [meta, setMeta] = useState({ branches: [], departments: [] });
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [hrSel, setHrSel] = useState([]);
  const [dateField, setDateField] = useState("added"); // added | updated
  const hrUsers = useHROptions(user?.role !== "recruiter");

  const load = async () => {
    const { data } = await api.get("/candidates");
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/requisitions").then((r) => setReqs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return items.filter((c) => {
      if (branch !== "All" && c.branch !== branch && user?.role !== "recruiter") return false;
      const filterDate = dateField === "updated" ? (c.updated_at || c.lead_date) : stageDate(c, stage);
      if (!inPeriod(filterDate, range)) return false;
      if (!matchesHR(c.recruiter_id, hrSel)) return false;
      if (q && !`${c.name} ${c.role_applied} ${c.source} ${c.contact || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (stage === "leads") return true;
      if (stage === "screened") return c.screening_status === "Done";
      if (stage === "l1") return ["Cleared", "Selected"].includes(c.l1_status);
      if (stage === "l2") return ["Cleared", "Selected"].includes(c.l2_status);
      if (stage === "finalized") return c.final_decision === "Selected";
      if (stage === "joined") return c.joining_status === "Done";
      if (stage === "rejected") return [c.screening_status, c.l1_status, c.l2_status, c.final_decision].includes("Rejected");
      return true;
    });
  }, [items, q, stage, branch, user, range, hrSel]);

  const summary = useMemo(() => {
    const base = items.filter((c) =>
      (branch === "All" || c.branch === branch || user?.role === "recruiter") &&
      matchesHR(c.recruiter_id, hrSel)
    );
    const inR = (d) => inPeriod(d, range);
    return {
      total: base.filter((c) => inR(c.lead_date)).length,
      screened: base.filter((c) => c.screening_status === "Done" && inR(stageDate(c, "screened"))).length,
      l1: base.filter((c) => ["Cleared", "Selected"].includes(c.l1_status) && inR(stageDate(c, "l1"))).length,
      l2: base.filter((c) => ["Cleared", "Selected"].includes(c.l2_status) && inR(stageDate(c, "l2"))).length,
      finalized: base.filter((c) => c.final_decision === "Selected" && inR(stageDate(c, "finalized"))).length,
      joined: base.filter((c) => c.joining_status === "Done" && inR(stageDate(c, "joined"))).length,
      rejected: base.filter((c) => [c.screening_status, c.l1_status, c.l2_status, c.final_decision].includes("Rejected") && inR(stageDate(c, "rejected"))).length,
    };
  }, [items, branch, user, range, hrSel]);

  const saveCandidate = async (form) => {
    if (!editing?.id && !form.recruiter_id) {
      toast.error("HR Name required hai — select karo kis HR ne ye candidate add kiya");
      return;
    }
    try {
      if (editing?.id) {
        await api.patch(`/candidates/${editing.id}`, form);
        toast.success("Candidate updated");
      } else {
        await api.post("/candidates", form);
        toast.success("Candidate added");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this candidate?")) return;
    await api.delete(`/candidates/${id}`);
    toast.success("Deleted");
    setOpen(false);
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="candidates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Recruitment CRM</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Candidate Pipeline</h1>
          <p className="text-slate-600 text-sm mt-1">
            {user?.role === "recruiter" ? "Your assigned candidates." : "All candidates across recruiters."}
          </p>
        </div>
        <div className="flex gap-2 items-center">
        <Button onClick={() => { setEditing({ recruiter_id: hrSelf?.id || "", recruiter_name: hrSelf?.name || "" }); setOpen(true); }} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-candidate-button">
          <Plus className="h-4 w-4 mr-1" /> Add candidate
        </Button>
        <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-full" data-testid="bulk-candidates-button">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Bulk import
        </Button>
      </div>
      </div>

      {/* Stage summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2 bg-white border border-slate-200 rounded-lg p-3">
        {[
          ["Total Leads", summary.total, "all"],
          ["Screened", summary.screened, "screened"],
          ["L1", summary.l1, "l1"],
          ["L2", summary.l2, "l2"],
          ["Finalized", summary.finalized, "finalized"],
          ["Joined", summary.joined, "joined"],
          ["Rejected", summary.rejected, "rejected"],
        ].map(([label, val, s]) => (
          <button
            key={label}
            onClick={() => setStage(s)}
            className={`text-left px-3 py-2 rounded-md border transition-colors ${
              stage === s ? "border-[var(--yc-navy)] bg-slate-50" : "border-transparent hover:bg-slate-50"
            }`}
            data-testid={`stage-filter-${s}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="font-heading text-xl font-bold text-[var(--yc-navy)]">{val}</div>
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 items-end flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search name, role, mobile, source…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white"
            data-testid="candidate-search-input"
          />
        </div>
        <PeriodFilter preset={preset} onPreset={setPreset} range={range} onRange={setRange} testId="cand-period-filter" />
        <div className="w-40">
          <Label className="label-eyebrow">Filter by</Label>
          <Select value={dateField} onValueChange={setDateField}>
            <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid="cand-date-field-toggle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="added">Added Date</SelectItem>
              <SelectItem value="updated">Updated Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {user?.role !== "recruiter" && <HRMultiFilter value={hrSel} onChange={setHrSel} users={hrUsers} testId="cand-hr-filter" />}
        {(range.from || hrSel.length > 0 || stage !== "all" || q) && (
          <button
            onClick={() => { setPreset("all"); setRange({ from: "", to: "" }); setHrSel([]); setStage("all"); setQ(""); setDateField("added"); }}
            className="h-9 px-3 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="cand-reset-filters"
          >
            Reset filters
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500" data-testid="candidate-count">
        Showing <span className="font-semibold text-[var(--yc-navy)]">{filtered.length}</span> candidate{filtered.length === 1 ? "" : "s"}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Candidate</th>
                <th className="data-cell">Role · Branch</th>
                <th className="data-cell">Source</th>
                <th className="data-cell">Screening</th>
                <th className="data-cell">L1</th>
                <th className="data-cell">L2</th>
                <th className="data-cell">Final</th>
                <th className="data-cell">Joining</th>
                <th className="data-cell">Days</th>
                <th className="data-cell">Added By (HR)</th>
                <th className="data-cell"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="data-cell text-center text-slate-500 py-10">No data found for selected filters.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 data-row" data-testid={`candidate-row-${c.id}`}>
                  <td className="data-cell">
                    <div className="font-medium text-[var(--yc-navy)]">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.contact} · {c.current_location}</div>
                  </td>
                  <td className="data-cell">
                    <div className="text-[var(--yc-navy)]">{c.role_applied}</div>
                    <div className="text-xs text-slate-500">{c.branch}</div>
                  </td>
                  <td className="data-cell text-slate-600">{c.source}</td>
                  <td className="data-cell"><Pill status={c.screening_status} type="screening" /></td>
                  <td className="data-cell">{c.l1_status ? <Pill status={c.l1_status} type="l1" /> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="data-cell">{c.l2_status ? <Pill status={c.l2_status} type="l2" /> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="data-cell"><Pill status={c.final_decision} type="final" /></td>
                  <td className="data-cell">
                    <Pill status={c.joining_status} type="joining" />
                    {c.converted_employee_id && (
                      <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--yc-emerald)] mt-1" data-testid={`cand-converted-${c.id}`}>In Employees ✓</div>
                    )}
                  </td>
                  <td className="data-cell text-slate-600">{c.days_in_pipeline ?? "—"}</td>
                  <td className="data-cell text-slate-600 text-xs">{c.recruiter_name || "Unassigned"}</td>
                  <td className="data-cell">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-candidate-${c.id}`}>
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CandidateDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        onSave={saveCandidate}
        meta={meta}
        reqs={reqs}
        onDelete={remove}
        hrUsers={hrUsers}
        canAssignHR={user?.role !== "recruiter"}
      />
      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/candidates/bulk"
        entityLabel="candidates"
        sampleHeaders={CANDIDATE_TEMPLATE}
        onDone={load}
      />
    </div>
  );
}

function Pill({ status, type }) {
  if (!status) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${badgeCls(status, type)}`}>
      {status}
    </span>
  );
}

function CandidateDialog({ open, onOpenChange, editing, onSave, meta, reqs, onDelete, hrUsers = [], canAssignHR = false }) {
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(editing || {});
    if (editing?.id) loadFiles(editing.id); else setFiles([]);
  }, [editing]);

  const loadFiles = async (cid) => {
    try {
      const { data } = await api.get(`/files/list?entity=candidate&entity_id=${cid}`);
      setFiles(data);
    } catch { /* ignore */ }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/files/upload?entity=candidate&entity_id=${editing.id}&doc_type=Resume`, fd);
      toast.success("File uploaded");
      loadFiles(editing.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (fid) => {
    await api.delete(`/files/${fid}`);
    loadFiles(editing.id);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing?.id ? "Edit candidate" : "New candidate"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name"><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="cand-name-input" /></Field>
          <Field label="Contact"><Input value={form.contact || ""} onChange={(e) => set("contact", e.target.value)} /></Field>
          <Field label="Role applied">
            <Select value={form.role_applied || ""} onValueChange={(v) => {
              set("role_applied", v);
              const r = reqs.find(r => r.role_title === v);
              if (r) { set("department", r.department); set("branch", r.branch); }
            }}>
              <SelectTrigger data-testid="cand-role-select"><SelectValue placeholder="Choose an open role" /></SelectTrigger>
              <SelectContent>
                {reqs.map((r) => <SelectItem key={r.id} value={r.role_title}>{r.role_title} — {r.branch}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Branch">
            <Select value={form.branch || ""} onValueChange={(v) => set("branch", v)}>
              <SelectTrigger><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                {meta.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Current location"><Input value={form.current_location || ""} onChange={(e) => set("current_location", e.target.value)} /></Field>
          <Field label="Current/Previous salary"><Input type="number" value={form.current_salary || ""} onChange={(e) => set("current_salary", Number(e.target.value))} /></Field>
          <Field label="Currently working">
            <Select value={form.currently_working || ""} onValueChange={(v) => set("currently_working", v)}>
              <SelectTrigger><SelectValue placeholder="Y / N" /></SelectTrigger>
              <SelectContent><SelectItem value="Y">Yes</SelectItem><SelectItem value="N">No</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Notice period"><Input value={form.notice_period || ""} onChange={(e) => set("notice_period", e.target.value)} /></Field>
          <Field label="Work experience"><Input value={form.work_experience || ""} onChange={(e) => set("work_experience", e.target.value)} /></Field>
          <Field label="Expected salary"><Input type="number" value={form.expected_salary || ""} onChange={(e) => set("expected_salary", Number(e.target.value))} /></Field>
          <Field label="Purpose of leaving"><Input value={form.purpose_of_leaving || ""} onChange={(e) => set("purpose_of_leaving", e.target.value)} /></Field>
          <Field label="Source">
            <Select value={form.source || ""} onValueChange={(v) => set("source", v)}>
              <SelectTrigger data-testid="cand-source-select"><SelectValue placeholder="Where sourced" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {canAssignHR && (
            <Field label="HR Name * (kis HR ne add kiya)">
              <Select value={form.recruiter_id || ""} onValueChange={(v) => set("recruiter_id", v)}>
                <SelectTrigger data-testid="cand-hr-assign-select"><SelectValue placeholder="Select HR" /></SelectTrigger>
                <SelectContent>
                  {hrUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Screening status">
            <Select value={form.screening_status || ""} onValueChange={(v) => set("screening_status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SCREENING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Screening remark"><Input value={form.screening_remark || ""} onChange={(e) => set("screening_remark", e.target.value)} /></Field>
          <Field label="L1 date"><Input type="date" value={(form.l1_date || "").slice(0,10)} onChange={(e) => set("l1_date", e.target.value)} /></Field>
          <Field label="L1 status">
            <Select value={form.l1_status || ""} onValueChange={(v) => set("l1_status", v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{L_STATUS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="L2 date"><Input type="date" value={(form.l2_date || "").slice(0,10)} onChange={(e) => set("l2_date", e.target.value)} /></Field>
          <Field label="L2 status">
            <Select value={form.l2_status || ""} onValueChange={(v) => set("l2_status", v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{L_STATUS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Final decision">
            <Select value={form.final_decision || ""} onValueChange={(v) => set("final_decision", v)}>
              <SelectTrigger data-testid="cand-final-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FINAL.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Last salary (after final round, ₹/month)">
            <Input type="number" value={form.last_salary || ""} onChange={(e) => set("last_salary", Number(e.target.value))} placeholder="e.g., 35000" data-testid="cand-last-salary-input" />
          </Field>
          <Field label="Joining status">
            <Select value={form.joining_status || ""} onValueChange={(v) => set("joining_status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{JOINING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Joining date"><Input type="date" value={(form.joining_date || "").slice(0,10)} onChange={(e) => set("joining_date", e.target.value)} /></Field>
          <Field label="HR remark" full><Textarea value={form.hr_remark || ""} onChange={(e) => set("hr_remark", e.target.value)} rows={2} /></Field>
        </div>

        {editing?.id && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="label-eyebrow mb-3">Attached files (Resume, certificates)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-slate-50" data-testid={`file-${f.id}`}>
                  <FileText className="h-3.5 w-3.5 text-slate-500" />
                  <a href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${localStorage.getItem("yc_token")}`} target="_blank" rel="noreferrer" className="text-[var(--yc-navy)] hover:underline">
                    {f.original_filename}
                  </a>
                  <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-[var(--yc-rose)]"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {files.length === 0 && <div className="text-xs text-slate-500">No files yet.</div>}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-300 hover:bg-slate-50 cursor-pointer text-sm text-slate-600">
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload resume / certificate"}
              <input type="file" className="hidden" onChange={upload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-testid="cand-upload-input" />
            </label>
          </div>
        )}

        <DialogFooter>
          {editing?.id && (
            <Button variant="outline" className="mr-auto text-[var(--yc-rose)] border-red-200 hover:bg-red-50" onClick={() => onDelete(editing.id)} data-testid="delete-candidate-button">
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-candidate-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
